import { Map as ImmutableMap } from 'immutable';
import type { RecordOf } from 'immutable';
import React from 'react';
import type { ReactElement, ReactNode } from 'react';

import OkIcon from '@mui/icons-material/CheckCircleRounded';
import MatchIcon from '@mui/icons-material/CompareArrowsRounded';
import DeleteIcon from '@mui/icons-material/DeleteRounded';
import ErrorIcon from '@mui/icons-material/ErrorRounded';
import HelpIcon from '@mui/icons-material/HelpRounded';
import InfoIcon from '@mui/icons-material/InfoRounded';
import WarningIcon from '@mui/icons-material/WarningRounded';

import { mkRootUiData } from 'app-components/flux/root-ui/rootUiTypes';
import type { RootUiData } from 'app-components/flux/root-ui/rootUiTypes';
import { getLogger } from 'app-components/utils/core';

import { DIALOG_TYPE as ALERT_DIALOG_TYPE, mkAlertDialogProps } from 'components/Dialogs/AlertDialog/types';
import type { AlertDialogCallback } from 'components/Dialogs/AlertDialog/types';
import { dangerouslyGetTheme } from 'themes/themesSelectors';

import type { AlertDialogIcon } from './types';

const log = getLogger('components/QDialogs/core');

// Alerts have never shown these icons. Turning them on is a design change.
const SHOW_TITLE_ICONS = false;

const getDialogIcon = (titleIcon: AlertDialogIcon): ReactElement | null => {
  const theme = dangerouslyGetTheme();
  const iconStyle = {
    marginTop: -3,
    marginRight: 10,
    color: theme.palette.icon.main,
  };

  switch (titleIcon) {
    case 'delete':
      return <DeleteIcon style={iconStyle} />;
    case 'warning':
      return <WarningIcon style={iconStyle} />;
    case 'match':
      return <MatchIcon style={iconStyle} />;
    case 'info':
      return <InfoIcon style={iconStyle} />;
    case 'ok':
      return <OkIcon style={iconStyle} />;
    case 'help':
      return <HelpIcon style={iconStyle} />;
    case 'error':
      return <ErrorIcon style={iconStyle} />;

    default:
      return null;
  }
};

/** Builds the root UI dialog for `DialogAlert`; the arguments are the same. */
export const mkDialogAlertData = (
  title: string,
  content: ReactNode,
  onClose?: AlertDialogCallback | null,
  buttons: readonly string[] = ['No', 'Yes'],
  titleIcon: AlertDialogIcon = 'info',
  showCloseButton = false,
  tertiaryButton = '',
): RecordOf<RootUiData> => {
  const dialogData = mkRootUiData({
    type: ALERT_DIALOG_TYPE,
    allowNesting: true,
    id: title,
    props: ImmutableMap(
      mkAlertDialogProps({
        title,
        titleIcon: SHOW_TITLE_ICONS ? getDialogIcon(titleIcon) : null,
        content,
        onClose: onClose || (() => {}),
        buttons: onClose ? buttons : ['Ok'],
        showCloseButton,
        tertiaryButton,
      }),
    ),
  });
  log.debug('Creating Dialog...', dialogData.toJS());
  return dialogData;
};
